import React from 'react';
import { useSelector } from 'react-redux';
import debug from 'debug';

import { useGoErpEventStream } from 'services/GoErp/EventSource';
import { getClientCode } from 'reducers/Login';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getSelectedPosID } from 'reducers/PointsOfSale';

const DEBUG_SSE_CONNECTION = debug.enabled('SSE_DEBUG');

/**
 * A single channel for all SSE connections to go through.
 *
 * By convention, port1 is the originator, so in this case port1 connects to POS logic
 * and port2 connects to actual endpoints such as the goERP event stream
 */
export const SseMessageChannel = new window.MessageChannel();
// ports queue up messages until 'started'
// assigning to .onmessage starts the port implicitly, but using addEventListener does not
// To avoid any confusing behaviour, just open the ports immediately
SseMessageChannel.port1.start();
SseMessageChannel.port2.start();

/** Hook up a one-way connection from goERP to the POS */
function useGoerpConnections() {
  const clientCode = useSelector(getClientCode);
  const whId = useSelector(getSelectedWarehouseID);
  const posId = useSelector(getSelectedPosID);
  // Page and stream as defined by goERP CRM app developer Mamta
  // In the future IMO we should have a non-app-specific page
  // ideally in a shared 'asset' that all the apps can depend on
  // but for now this is fine
  useGoErpEventStream(
    'pos-users-ssed-data-page',
    `${clientCode}-pos-brazil-sse`,
    msg => SseMessageChannel.port2.postMessage(msg),
  );
  useGoErpEventStream(
    'pos-users-ssed-data-page',
    `${clientCode}-pos-brazil-sse:wh-${whId}`,
    msg => SseMessageChannel.port2.postMessage(msg),
  );
  useGoErpEventStream(
    'pos-users-ssed-data-page',
    `${clientCode}-pos-brazil-sse:pos-${posId}`,
    msg => SseMessageChannel.port2.postMessage(msg),
  );
}

/**
 * Connect up to devtools.
 * Specifically, log messages coming out of either end of the messagechannel
 * and expose a method 'postSseMessage' on window that developers can call to
 * simulate messages being sent to the POS
 */
function useDevtoolsConnection() {
  React.useEffect(() => {
    function posRecv(msg) {
      console.log('[DEVTOOLS SSE]: sent', msg.data);
    }
    function remoteRecv(msg) {
      console.log('[DEVTOOLS SSE]: recv', msg.data);
    }

    SseMessageChannel.port1.addEventListener('message', posRecv);
    SseMessageChannel.port2.addEventListener('message', remoteRecv);
    (window as any).postSseMessage = msg => {
      SseMessageChannel.port2.postMessage(msg);
    };
    return function cleanup() {
      SseMessageChannel.port1.removeEventListener('message', posRecv);
      SseMessageChannel.port2.removeEventListener('message', remoteRecv);
      delete (window as any).postSseMessage;
    };
  }, []);
}
/** Hook up all the remote connections to the {@link SseMessageChannel} */
export function useSseConnection() {
  useGoerpConnections();
  if (DEBUG_SSE_CONNECTION) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useDevtoolsConnection();
  }
}

(window as any).SseMessageChannel = SseMessageChannel;
